import { CustomTransactionItemsErrorResponse } from '@/context/services/transaction_items/type';
import {
  ForbiddenCodeEnum,
  isAuthError,
  isForbiddenError,
  isSuccess,
} from '@/context/services/type';
import { useTranslation } from '@/i18n';
import { useNavigate, useParams } from '@/router';
import { useToastAPI } from '@moneyforward/ap-frontend-components';
import {
  ApGeneralInputOption,
  PostApInvoiceTransactionGeneralInputsBatchOptionsBodyItem,
  PostApInvoiceTransactionGeneralInputsBatchOptionsQueryError,
  useDeleteApInvoiceTransactionGeneralInput,
  usePostApInvoiceTransactionGeneralInputsBatchOptions,
} from 'ap-openapi';
import { useCallback, useEffect } from 'react';

export type TransactionItemState = {
  transaction_item_deleted?: boolean;
};

export enum TransactionItemStatus {
  SUCCESS = 'success',
  INVALID = 'invalid',
  AUTH_ERROR = 'auth_error',
  ERROR = 'error',
  PROXY_LOGIN_ERROR = 'proxy_login_error',
}

type TransactionItemDeleteSuccess = {
  status: TransactionItemStatus.SUCCESS;
};

type TransactionItemDeleteProxyLoginError = {
  status: TransactionItemStatus.PROXY_LOGIN_ERROR;
  errors: CustomTransactionItemsErrorResponse;
};

type TransactionItemDeleteAuthError = {
  status: TransactionItemStatus.AUTH_ERROR;
  redirectPath: string;
};

type TransactionItemDeleteInvalid = {
  status: TransactionItemStatus.INVALID;
  errors: CustomTransactionItemsErrorResponse;
};

type TransactionItemDeleteError = {
  status: TransactionItemStatus.ERROR;
};

type TransactionItemDeleteResult =
  | TransactionItemDeleteSuccess
  | TransactionItemDeleteProxyLoginError
  | TransactionItemDeleteAuthError
  | TransactionItemDeleteInvalid
  | TransactionItemDeleteError;

export const useTransactionItemDeleteNotification = (
  state: TransactionItemState | null
) => {
  const notify = useToastAPI();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { report_type_id } = useParams(
    '/report_types/:report_type_id/transaction_items'
  );

  useEffect(() => {
    if (state?.transaction_item_deleted && report_type_id) {
      notify.success({
        title: t('transaction_items.delete.success'),
        duration: 5,
      });
      navigate('/report_types/:report_type_id/transaction_items', {
        params: { report_type_id },
        state: null,
      });
    }
  }, [state, notify, t, navigate, report_type_id]);
};

export const useDeleteTransactionItem = (): ((
  transaction_item_id?: string
) => Promise<TransactionItemDeleteResult>) => {
  const { mutateAsync: deleteTransactionItem } =
    useDeleteApInvoiceTransactionGeneralInput();

  return useCallback(
    async (
      transaction_item_id: string = ''
    ): Promise<TransactionItemDeleteResult> => {
      const resp = await deleteTransactionItem({ id: transaction_item_id });

      if (isSuccess(resp.status)) {
        return { status: TransactionItemStatus.SUCCESS };
      } else if (isAuthError(resp.status) || isForbiddenError(resp.status)) {
        // @ts-ignore - resp.data might not have code property in success types
        if (resp.data?.code === ForbiddenCodeEnum.FORBIDDEN_MIMIC_MODE) {
          return {
            status: TransactionItemStatus.PROXY_LOGIN_ERROR,
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            errors: (resp.data ?? {}) as CustomTransactionItemsErrorResponse,
          };
        }
        return {
          status: TransactionItemStatus.AUTH_ERROR,
          redirectPath: resp.headers?.location ?? '/',
        };
      } else {
        return { status: TransactionItemStatus.ERROR };
      }
    },
    [deleteTransactionItem]
  );
};

export type UseGetPulldownLabelsResult = {
  data: Array<Record<string, ApGeneralInputOption[]>>;
  isLoading: boolean;
  error: PostApInvoiceTransactionGeneralInputsBatchOptionsQueryError | null;
};

export const useInvoiceTransactionGeneralInputsBatchOptions = (
  pulldownBatchBody: PostApInvoiceTransactionGeneralInputsBatchOptionsBodyItem[]
): UseGetPulldownLabelsResult => {
  const { data, isLoading, error } =
    usePostApInvoiceTransactionGeneralInputsBatchOptions(pulldownBatchBody, {
      query: {
        enabled: pulldownBatchBody.length > 0,
      },
    });

  return {
    data: data?.data ?? [],
    isLoading: isLoading,
    error: error,
  };
};
